import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { X, Check, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const CandidatureActionModal = ({ isOpen, onClose, onSuccess, candidature, actionType }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    manager_email: candidature?.manager_email || candidature?.email || '',
    manager_telephone: candidature?.manager_telephone || candidature?.telephone || '',
    raison_refus: ''
  });

  if (!isOpen || !candidature) return null;

  // Check if coordinates are missing for "accepte" action
  const needsCoordinates = actionType === 'accepte' && 
    (!candidature.email && !candidature.manager_email) || 
    (!candidature.telephone && !candidature.manager_telephone);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    // Validation for "accepte" action only if coordinates are needed
    if (actionType === 'accepte' && needsCoordinates) {
      if (!formData.manager_email.trim()) {
        toast.error('L\'email est requis');
        return;
      }
      if (!formData.manager_telephone.trim()) {
        toast.error('Le téléphone est requis');
        return;
      }
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        status: actionType,
        ...(actionType === 'accepte' && needsCoordinates && {
          manager_email: formData.manager_email.trim(),
          manager_telephone: formData.manager_telephone.trim()
        }),
        ...(actionType === 'refuse' && formData.raison_refus.trim() && {
          raison_refus: formData.raison_refus.trim()
        })
      };

      const response = await fetch(`${API}/candidatures/${candidature.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const actionText = actionType === 'accepte' ? 'acceptée' : 'refusée';
        toast.success(`Candidature ${actionText} avec succès`);
        onSuccess();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating candidature status:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form
    setFormData({
      manager_email: '',
      manager_telephone: '',
      raison_refus: ''
    });
    onClose();
  };

  const getModalTitle = () => {
    if (actionType === 'accepte') {
      return 'Accepter la candidature';
    }
    return 'Refuser la candidature';
  };

  const getModalIcon = () => {
    if (actionType === 'accepte') {
      return <Check size={20} className="text-green-600" />;
    }
    return <XCircle size={20} className="text-red-600" />;
  };

  return (
    <div className="candidature-action-modal-overlay">
      <div className="candidature-action-modal">
        <Card className="candidature-action-modal-card">
          <CardHeader className="candidature-action-modal-header">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                {getModalIcon()}
                <CardTitle className="candidature-action-modal-title">
                  {getModalTitle()}
                </CardTitle>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="candidature-action-modal-close"
              >
                <X size={20} />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="candidature-action-modal-content">
            {/* Candidat Info */}
            <div className="candidature-info-section">
              <h4 className="font-semibold mb-2 text-sm">Candidat :</h4>
              <p className="text-sm text-gray-700 mb-4">
                {candidature.prenom} {candidature.nom}
              </p>
            </div>

            <form className="candidature-action-form">
              {actionType === 'accepte' && needsCoordinates && (
                <>
                  <div className="form-group">
                    <Label htmlFor="manager_email" className="form-label">
                      E-mail du candidat *
                    </Label>
                    <Input
                      id="manager_email"
                      type="email"
                      value={formData.manager_email}
                      onChange={(e) => handleInputChange('manager_email', e.target.value)}
                      className="form-input"
                      placeholder=""
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ces coordonnées seront transmises au service RH
                    </p>
                  </div>

                  <div className="form-group">
                    <Label htmlFor="manager_telephone" className="form-label">
                      Téléphone du candidat *
                    </Label>
                    <Input
                      id="manager_telephone"
                      type="tel"
                      value={formData.manager_telephone}
                      onChange={(e) => handleInputChange('manager_telephone', e.target.value)}
                      className="form-input"
                      placeholder=""
                    />
                  </div>
                </>
              )}

              {actionType === 'accepte' && !needsCoordinates && (
                <div className="form-group">
                  <p className="text-sm text-gray-700 mb-4">
                    Les coordonnées du candidat sont déjà disponibles.
                    Confirmer l'acceptation de cette candidature ?
                  </p>
                  <div className="existing-coordinates">
                    <p className="text-xs text-gray-600">
                      <strong>Email :</strong> {candidature.manager_email || candidature.email || 'Non renseigné'}
                    </p>
                    <p className="text-xs text-gray-600">
                      <strong>Téléphone :</strong> {candidature.manager_telephone || candidature.telephone || 'Non renseigné'}
                    </p>
                  </div>
                </div>
              )}

              {actionType === 'refuse' && (
                <div className="form-group">
                  <Label htmlFor="raison_refus" className="form-label">
                    Raison du refus (facultatif)
                  </Label>
                  <Textarea
                    id="raison_refus"
                    value={formData.raison_refus}
                    onChange={(e) => handleInputChange('raison_refus', e.target.value)}
                    className="form-input"
                    rows={3}
                    placeholder="Indiquez la raison du refus si nécessaire..."
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="candidature-action-modal-actions">
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className={actionType === 'accepte' ? 'btn-accept' : 'btn-refuse'}
                  style={{
                    backgroundColor: actionType === 'accepte' ? '#22c55e' : '#ef4444',
                    borderColor: actionType === 'accepte' ? '#22c55e' : '#ef4444'
                  }}
                >
                  {actionType === 'accepte' ? (
                    <Check size={16} className="mr-2" />
                  ) : (
                    <XCircle size={16} className="mr-2" />
                  )}
                  {loading ? 'Traitement...' : (actionType === 'accepte' ? 'Accepter' : 'Refuser')}
                </Button>
                <Button
                  type="button"
                  onClick={handleCancel}
                  disabled={loading}
                  variant="outline"
                  className="btn-cancel"
                >
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CandidatureActionModal;